import type { FC } from 'react';
import { useId } from 'react';
import { Tooltip } from 'react-tooltip';

import { AcceptIconTeacher, AcceptIconTeacherActive } from 'ui/icons/Accept.Icon';

import classes from './VerificationStatusBadge.module.scss';

type ProfileRole = 'student' | 'tutor';

type Props = {
  status?: string | null;
  comment?: string | null;
  role?: ProfileRole;
  place?: 'top' | 'right' | 'bottom' | 'left';
};

type StatusConfig = {
  label: string;
  variant: 'pending' | 'success' | 'error' | 'warning';
  tooltip: string;
  activeIcon?: boolean;
};

const TUTOR_STATUS_CONFIG: Record<string, StatusConfig> = {
  draft: {
    label: 'Аккаунт не подтвержден',
    variant: 'warning',
    tooltip:
      'Заполните профиль: добавьте минимум одно образование и одну услугу (предмет с ценой), затем сохраните изменения - после этого профиль отправится на модерацию.',
  },
  pending_review: {
    label: 'На модерации',
    variant: 'pending',
    tooltip:
      'Администраторы проверяют ваш профиль. До одобрения аккаунт не отображается в поиске.',
  },
  approved: {
    label: 'Профиль одобрен',
    variant: 'success',
    tooltip: 'Модерация пройдена - аккаунт публичный.',
    activeIcon: true,
  },
  rejected: {
    label: 'Профиль отклонен',
    variant: 'error',
    tooltip: 'Исправьте замечания и отправьте профиль на повторную проверку.',
  },
  changes_requested: {
    label: 'Нужны правки',
    variant: 'warning',
    tooltip: 'Внесите изменения по комментарию модератора и сохраните профиль.',
  },
};

const STUDENT_STATUS_CONFIG: Record<string, StatusConfig> = {
  draft: {
    label: 'Аккаунт не подтвержден',
    variant: 'warning',
    tooltip:
      'Заполните имя и пол (или данные детей, если вы родитель), затем сохраните изменения - после этого профиль отправится на модерацию.',
  },
  pending_review: {
    label: 'На модерации',
    variant: 'pending',
    tooltip:
      'Администраторы проверяют ваш профиль. До одобрения заявки и чаты будут недоступны.',
  },
  approved: {
    label: 'Профиль одобрен',
    variant: 'success',
    tooltip: 'Модерация пройдена - можно создавать заявки и пользоваться чатами.',
    activeIcon: true,
  },
  rejected: {
    label: 'Профиль отклонен',
    variant: 'error',
    tooltip: 'Исправьте замечания модератора и сохраните профиль для повторной проверки.',
  },
  changes_requested: {
    label: 'Нужны правки',
    variant: 'warning',
    tooltip: 'Внесите изменения по комментарию модератора и сохраните профиль.',
  },
};

export const VerificationStatusBadge: FC<Props> = ({
  status,
  comment,
  role = 'tutor',
  place = 'bottom',
}) => {
  const tooltipId = useId();
  const key = status || 'draft';
  const statusMap = role === 'student' ? STUDENT_STATUS_CONFIG : TUTOR_STATUS_CONFIG;
  const config = statusMap[key] ?? statusMap.draft;

  const tooltipText =
    comment && (key === 'rejected' || key === 'changes_requested')
      ? `${config.tooltip}\n\nКомментарий: ${comment}`
      : config.tooltip;

  return (
    <>
      <span
        className={`${classes.badge} ${classes[`badge--${config.variant}`]}`}
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltipText}
        aria-label={config.label}
      >
        <span className={classes.badge__icon}>
          {config.activeIcon ? <AcceptIconTeacherActive /> : <AcceptIconTeacher />}
        </span>
        <span className={classes.badge__label}>{config.label}</span>
      </span>
      <Tooltip
        id={tooltipId}
        className={classes.tooltip}
        place={place}
        clickable
      />
    </>
  );
};
